// client/src/components/bankAccounts/BankAccountList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import bankAccountService from '../../services/bankAccountService';

const BankAccountList = () => {
  const { t } = useTranslation();
  const { isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  // Function to load bank accounts
  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await bankAccountService.getBankAccounts();
      setBankAccounts(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingBankAccounts'));
    } finally {
      setLoading(false);
    }
  };

  // Load bank accounts on component mount
  useEffect(() => {
    loadBankAccounts();
  }, []);

  // Handle bank account deletion
  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await bankAccountService.deleteBankAccount(accountToDelete.id);
      setSuccess(t('bankAccountDeletedSuccess'));
      loadBankAccounts();
    } catch (err) {
      setError(t('errorDeletingBankAccount'));
    } finally {
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('bankAccounts')}
      </Typography>

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add Bank Account button - only for admins */}
      {isAdmin() && (
        <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/bank-accounts/create"
          >
            {t('addBankAccount')}
          </Button>
        </Box>
      )}

      {/* Bank Accounts Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : bankAccounts.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noBankAccountsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('accountName')}</TableCell>
                <TableCell>{t('accountNumber')}</TableCell>
                <TableCell>{t('referenceNo1')}</TableCell>
                <TableCell>{t('referenceNo2')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bankAccounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>{account.name}</TableCell>
                  <TableCell>{account.account_number}</TableCell>
                  <TableCell>{account.reference_no1}</TableCell>
                  <TableCell>{account.reference_no2}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      component={RouterLink}
                      to={`/bank-accounts/edit/${account.id}`}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(account)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteBankAccountConfirmation', { 
              name: accountToDelete?.name 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BankAccountList;